import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { KanbanSquare, List, Plus, RefreshCw, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
} from '@/components/ui'
import { useRmaList, useRmaStats, useTransitionRma } from '../api/queries'
import type { RmaSummary, Stage } from '../api/types'
import {
  BOARD_STAGES,
  REASON_LABELS,
  STAGE_LABELS,
  STAGE_VARIANT,
} from '../utils/stage-meta'
import { RmaCreateDrawer } from '../components/RmaCreateDrawer'
import { RmaDetailDrawer } from '../components/RmaDetailDrawer'
import { RmaKanban } from '../components/RmaKanban'

type ViewMode = 'table' | 'kanban'

const VIEW_OPTIONS = [
  { value: 'table' as const, label: 'Table', icon: List },
  { value: 'kanban' as const, label: 'Kanban', icon: KanbanSquare },
]

type StageFilter = 'all' | Stage

const STAGE_FILTER_OPTIONS: { value: StageFilter; label: string }[] = [
  { value: 'all', label: 'All stages' },
  ...BOARD_STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] })),
]

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function ReturnsRmaPage() {
  const [view, setView] = useState<ViewMode>('kanban')
  const [stageFilter, setStageFilter] = useState<StageFilter>('all')
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const stats = useRmaStats()
  const list = useRmaList(stageFilter === 'all' ? {} : { status: stageFilter })
  const transition = useTransitionRma()

  const items = list.data?.items ?? []

  const counts = stats.data?.counts_by_status ?? {}
  const openCount =
    (counts.requested ?? 0) + (counts.approved ?? 0) + (counts.rma_issued ?? 0)
  const inTransitCount = counts.return_received ?? 0
  const needsDispositionCount = counts.disposition_set ?? 0
  const total = stats.data?.total ?? 0

  const columns = useRmaColumns()

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <PageHeaderBand
        title="Returns / RMA"
        description="Return authorizations for material coming back from customers — issue numbers, generate documentation, and track through resolution."
      >
        <div className="ml-auto">
          <MetricStrip variant="inline">
            <MetricCard variant="inline" label="Open" value={String(openCount)} />
            <MetricCard variant="inline" label="Awaiting return" value={String(inTransitCount)} />
            <MetricCard variant="inline" label="Needs disposition" value={String(needsDispositionCount)} />
            <MetricCard variant="inline" label="Total" value={String(total)} />
          </MetricStrip>
        </div>
      </PageHeaderBand>

      <Toolbar>
        <ToolbarLeft>
          <Select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as StageFilter)}
            aria-label="Filter by stage"
          >
            {STAGE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            <RefreshCw size={14} className="mr-1.5" />
            Refresh
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          <ViewToggle<ViewMode>
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
            aria-label="View mode"
          />
          <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus size={14} className="mr-1.5" />
            New RMA
          </Button>
        </ToolbarRight>
      </Toolbar>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {list.isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="m-0 text-sm text-tertiary">Loading…</p>
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={RotateCcw}
            title="No RMAs here yet"
            description={
              stageFilter === 'all'
                ? 'Create an RMA when a customer asks to return material — CARLA has no autonomous entry point for this yet, so start it here.'
                : `No RMA records match the "${STAGE_FILTER_OPTIONS.find((o) => o.value === stageFilter)?.label ?? stageFilter}" filter.`
            }
            action={
              stageFilter === 'all' ? (
                <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus size={14} className="mr-1.5" />
                  New RMA
                </Button>
              ) : null
            }
          />
        ) : view === 'kanban' ? (
          <RmaKanban
            items={items}
            selectedId={selectedId ?? undefined}
            onSelect={(rma) => setSelectedId(rma.id)}
            onTransition={(rma, target) => {
              transition.mutate(
                { rmaId: rma.id, targetStage: target },
                {
                  onError: (err) => {
                    const msg = err instanceof Error ? err.message : 'transition failed'
                    toast.error(msg, { duration: 5000 })
                  },
                },
              )
            }}
          />
        ) : (
          <DataTable
            data={items}
            columns={columns}
            loading={list.isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => setSelectedId(r.id)}
            moduleId="returns-rma"
            maxHeight="100%"
            columnWidths={{
              rma_number: 130,
              customer: 220,
              reason: 180,
              line_count: 70,
              stage: 130,
              created_at: 110,
            }}
            minTableWidth={1000}
            emptyState={
              <EmptyState variant="no-match" title="No matches" description="Try clearing the stage filter." />
            }
          />
        )}
      </div>

      <RmaCreateDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(rmaId) => setSelectedId(rmaId)}
      />
      <RmaDetailDrawer
        rmaId={selectedId}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null)
        }}
      />
    </div>
  )
}

function useRmaColumns(): ColumnDef<RmaSummary, unknown>[] {
  return useMemo<ColumnDef<RmaSummary, unknown>[]>(
    () => [
      {
        id: 'rma_number',
        header: 'RMA #',
        accessorFn: (r) => r.rma_number ?? '',
        cell: (ctx) => (
          <span className="text-secondary">{ctx.row.original.rma_number ?? '—'}</span>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        accessorFn: (r) => r.customer_name ?? '',
        cell: (ctx) =>
          ctx.row.original.customer_name ?? <em className="text-tertiary">unknown</em>,
      },
      {
        id: 'reason',
        header: 'Reason',
        accessorFn: (r) => REASON_LABELS[r.reason_code],
        cell: (ctx) => (
          <span className="text-secondary">{REASON_LABELS[ctx.row.original.reason_code]}</span>
        ),
      },
      {
        id: 'line_count',
        header: 'Lines',
        accessorKey: 'line_count',
        meta: { numeric: true },
        cell: (ctx) => <span className="text-secondary">{ctx.row.original.line_count}</span>,
      },
      {
        id: 'stage',
        header: 'Stage',
        accessorFn: (r) => STAGE_LABELS[r.status],
        cell: (ctx) => (
          <Chip size="xs" variant={STAGE_VARIANT[ctx.row.original.status]} dot>
            {STAGE_LABELS[ctx.row.original.status]}
          </Chip>
        ),
      },
      {
        id: 'created_at',
        header: 'Created',
        accessorKey: 'created_at',
        cell: (ctx) => (
          <span className="text-tertiary">{formatDate(ctx.row.original.created_at)}</span>
        ),
      },
    ],
    [],
  )
}
